/*
 * Homepage quiz picker — accessible dropdown that routes each theme
 * straight to its quiz. Mirrors the heroapp-faq.js init pattern
 * (plain class, [data-*] init, shopify:section:load re-init).
 *
 * Wrapped in an IIFE: this loads as a plain <script defer> alongside
 * heroapp-faq.js (which also declares a top-level `init`) on the
 * homepage. Without isolation the second script to parse throws
 * "Identifier 'init' has already been declared" and never runs.
 */
(() => {
  class HomeQuizPicker {
  constructor(root) {
    this.root = root;
    this.trigger = root.querySelector('[data-hh-picker-trigger]');
    this.menu = root.querySelector('[data-hh-picker-menu]');
    if (!this.trigger || !this.menu) return;

    this.items = Array.from(this.menu.querySelectorAll('.hh-home-picker__item'));
    this.open = false;

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event));
    this.menu.addEventListener('keydown', (event) => this.onMenuKeydown(event));

    this.onDocPointerDown = (event) => {
      if (this.open && !this.root.contains(event.target)) this.close();
    };
    document.addEventListener('pointerdown', this.onDocPointerDown);
  }

  toggle() {
    if (this.open) {
      this.close();
    } else {
      this.show();
    }
  }

  show() {
    this.open = true;
    this.menu.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close({ focusTrigger = false } = {}) {
    this.open = false;
    this.menu.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger) this.trigger.focus();
  }

  focusItem(index) {
    if (this.items.length === 0) return;
    const clamped = (index + this.items.length) % this.items.length;
    this.items[clamped].focus();
  }

  onTriggerKeydown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.show();
      this.focusItem(0);
    }
  }

  onMenuKeydown(event) {
    const current = this.items.indexOf(document.activeElement);
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close({ focusTrigger: true });
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusItem(current + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusItem(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItem(this.items.length - 1);
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }
}

const init = () => {
  document.querySelectorAll('[data-hh-quiz-picker]').forEach((root) => {
    if (root.dataset.hhQuizPickerReady === 'true') return;
    root.dataset.hhQuizPickerReady = 'true';
    new HomeQuizPicker(root);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
})();
